// Top-bar alarm bell for human-in-the-loop (HITL) requests — the replacement
// for the full Inbox page (Burrow simplification, plan item 6). Global chrome:
// mounted in the shell top bar so it's reachable from every screen. A count
// badge of active HITL requests; clicking opens a popover where each row is the
// question, its options, and a respond action. Polls every 30s (no sockets).
//
// Two sources, both "a person must decide before work continues":
//  - Workflow runs: `GET /v1/hitl/pending` (a PAUSED run with a HITL context).
//    The per-row question + options come from `GET /v1/hitl/:runId`;
//    responding posts to `/v1/runs/:id/resume`.
//  - Persistent-agent asks (`request_human_input`):
//    `GET /v1/persistent-agent-elicitations/pending`. One-click answers are
//    the same ones the in-thread card offers; "Open" goes to the full answer
//    form for free-text / edit decisions.
import { useClient } from '@cortex/react-query';
import type {
  AnswerPersistentAgentElicitationInput,
  HitlContext,
  HitlQueueItem,
  IPaginated,
  PersistentAgentElicitationInboxRow,
} from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { elicitationQuickActions } from '@formant/chat';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { routes } from '../../../lib/constants';

const pollIntervalMs = 30_000;

/** Shared with the Inbox list + ElicitationResolveForm, whose answers invalidate it. */
const elicitationsPendingKey = ['elicitations', 'pending'];

export function HitlBell() {
  const client = useClient();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const pending = useQuery({
    queryKey: ['hitl', 'pending', 'bell'],
    queryFn: () =>
      client.http
        .get('v1/hitl/pending', { searchParams: { limit: '20' } })
        .json<IPaginated<HitlQueueItem>>(),
    refetchInterval: pollIntervalMs,
    refetchIntervalInBackground: true,
  });

  const elicitations = useQuery({
    queryKey: elicitationsPendingKey,
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-elicitations/pending')
        .json<PersistentAgentElicitationInboxRow[]>(),
    refetchInterval: pollIntervalMs,
    refetchIntervalInBackground: true,
  });

  const items = pending.data?.items ?? [];
  const asks = elicitations.data ?? [];
  const count = (pending.data?.total ?? 0) + asks.length;

  // Close on outside-click + Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="hitl-bell" ref={anchorRef}>
      <button
        type="button"
        className="hitl-bell__button fk-topbar__util"
        aria-label={count > 0 ? `${count} requests need a decision` : 'No decisions waiting'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <BellIcon />
        {count > 0 ? <span className="hitl-bell__badge">{count}</span> : null}
      </button>

      {open ? (
        <div className="hitl-bell__popover" role="dialog" aria-label="Decisions needing a person">
          <div className="hitl-bell__pop-head">
            <h3>Needs your decision</h3>
            {count > 0 ? <span className="hitl-bell__pop-count">{count}</span> : null}
          </div>
          {pending.isLoading || elicitations.isLoading ? (
            <div className="hitl-bell__empty">Checking…</div>
          ) : items.length === 0 && asks.length === 0 ? (
            <div className="hitl-bell__empty">Nothing is waiting on you right now.</div>
          ) : (
            <>
              {asks.map((row) => (
                <ElicitationRow key={row.id} row={row} onNavigate={() => setOpen(false)} />
              ))}
              {items.map((item) => (
                <HitlRow key={item.runId} item={item} />
              ))}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function HitlRow({ item }: { item: HitlQueueItem }) {
  const client = useClient();
  const qc = useQueryClient();

  // The queue row lacks the question + options; the detail endpoint carries them.
  const detail = useQuery({
    queryKey: ['hitl', item.runId],
    queryFn: () => client.http.get(`v1/hitl/${item.runId}`).json<HitlContext>(),
  });

  const respond = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      client.http.post(`v1/runs/${item.runId}/resume`, { json: body }).json<unknown>(),
    onSuccess: () => {
      fork(qc.invalidateQueries({ queryKey: ['hitl', 'pending', 'bell'] }));
      fork(qc.invalidateQueries({ queryKey: ['hitl', item.runId] }));
    },
  });

  const question = detail.data?.question ?? item.reason ?? 'A run is waiting for a decision.';
  const options = detail.data?.options ?? [];

  return (
    <div className="hitl-bell__row">
      <div className="hitl-bell__q">{question}</div>
      <div className="hitl-bell__src">
        <span>{item.workflowName}</span>
        <span aria-hidden="true">·</span>
        <span>{item.pausedAt ? relative(item.pausedAt) : 'paused'}</span>
      </div>
      <div className="hitl-bell__opts">
        {options.length > 0 ? (
          options.map((opt, i) => (
            <button
              key={`${opt.label}-${i}`}
              type="button"
              className={i === 0 ? 'hitl-bell__opt hitl-bell__opt--primary' : 'hitl-bell__opt'}
              disabled={respond.isPending}
              onClick={() => respond.mutate({ optionValue: opt.value })}
              title={opt.description}
            >
              {opt.label}
            </button>
          ))
        ) : (
          <button
            type="button"
            className="hitl-bell__opt hitl-bell__opt--primary"
            disabled={respond.isPending}
            onClick={() => respond.mutate({})}
          >
            {respond.isPending ? 'Resuming…' : 'Resume'}
          </button>
        )}
      </div>
      {respond.error ? (
        <div className="hitl-bell__error">Failed: {(respond.error as Error).message}</div>
      ) : null}
    </div>
  );
}

function ElicitationRow({
  row,
  onNavigate,
}: {
  row: PersistentAgentElicitationInboxRow;
  onNavigate: () => void;
}) {
  const client = useClient();
  const qc = useQueryClient();

  const respond = useMutation({
    mutationFn: (body: AnswerPersistentAgentElicitationInput) =>
      client.http
        .post(
          `v1/persistent-agent-groups/${row.agentId}/agents/${row.persistentAgentId}/elicitations/${row.id}/answer`,
          { json: body },
        )
        .json<unknown>(),
    onSuccess: () => {
      fork(qc.invalidateQueries({ queryKey: elicitationsPendingKey }));
    },
  });

  const actions = elicitationQuickActions(row);

  return (
    <div className="hitl-bell__row">
      <div className="hitl-bell__q hitl-bell__q--clamp" title={row.question}>
        {row.question}
      </div>
      <div className="hitl-bell__src">
        <Link
          to={routes.orchestrationThread(row.agentId, row.persistentAgentId)}
          onClick={onNavigate}
        >
          {row.persistentAgentName}
        </Link>
        <span aria-hidden="true">·</span>
        <span>{relative(row.createdAt)}</span>
      </div>
      <div className="hitl-bell__opts">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={a.primary ? 'hitl-bell__opt hitl-bell__opt--primary' : 'hitl-bell__opt'}
            disabled={respond.isPending}
            onClick={() => respond.mutate(a.body)}
            title={a.description}
          >
            {a.label}
          </button>
        ))}
        <Link
          to="/inbox/elicitation/$elicitationId"
          params={{ elicitationId: row.id }}
          className="hitl-bell__opt"
          onClick={onNavigate}
        >
          Open
        </Link>
      </div>
      {respond.error ? (
        <div className="hitl-bell__error">Failed: {(respond.error as Error).message}</div>
      ) : null}
    </div>
  );
}

function relative(iso: string | Date): string {
  const ms = new Date(iso).getTime() - Date.now();
  const sec = Math.round(Math.abs(ms) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function BellIcon() {
  return (
    <svg className="icon" width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 2a3.6 3.6 0 00-3.6 3.6c0 3-1.2 3.9-1.2 3.9h9.6s-1.2-.9-1.2-3.9A3.6 3.6 0 008 2z"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinejoin="round"
      />
      <path
        d="M6.7 12a1.4 1.4 0 002.6 0"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </svg>
  );
}
